import { AdvisorDto, IAdvisorClient } from '@mse-reg/clients';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createAppThunk, RootState } from '../../store';
import { addExceptionNotification } from '../notification.slice';

// State
const initialState = {
  loading: false,
  advisors: {} as { [uasId: number]: AdvisorDto[] },
};

export type AdvisorSelectionState = Readonly<typeof initialState>;

// Helper to sort advisors
const sortAdvisors = (advisors: AdvisorDto[]): AdvisorDto[] =>
  advisors
    .slice()
    .sort((a1, a2) =>
      a1.familyName === a2.familyName ? (a1.firstName < a2.firstName ? -1 : 1) : a1.familyName < a2.familyName ? -1 : 1,
    );

// Thunks
export const loadAdvisorsAsync = createAppThunk(
  'advisorSelection/loadAdvisors',
  async (uasId: number, deps: { advisorClient: IAdvisorClient }) => {
    const advisors = await deps.advisorClient.list(uasId);
    return { uasId, advisors: sortAdvisors(advisors) };
  },
  (r) => ({ advisorClient: r.resolve<IAdvisorClient>('IAdvisorClient') }),
);

export const getAdvisorsAsync = createAppThunk(
  'advisorSelection/getAdvisors',
  async (uasId: number, deps: { advisorClient: IAdvisorClient }, dispatch, getState) => {
    const state = getState() as RootState;
    const cached = state.advisorSelection.advisors[uasId];
    if (cached) {
      return { uasId, advisors: cached };
    }
    const result = await dispatch(loadAdvisorsAsync(uasId)).unwrap();
    return result;
  },
  (r) => ({ advisorClient: r.resolve<IAdvisorClient>('IAdvisorClient') }),
);

// Slice
const advisorSelectionSlice = createSlice({
  name: 'advisorSelection',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(loadAdvisorsAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        loadAdvisorsAsync.fulfilled,
        (state, action: PayloadAction<{ uasId: number; advisors: AdvisorDto[] }>) => {
          state.loading = false;
          state.advisors[action.payload.uasId] = action.payload.advisors;
        },
      )
      .addCase(loadAdvisorsAsync.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const advisorSelectionReducer = advisorSelectionSlice.reducer;
export const advisorSelectionActions = advisorSelectionSlice.actions;

// Thunks object for convenience
export const advisorSelectionThunks = {
  loadAdvisorsAsync,
  getAdvisorsAsync,
};

// Legacy module pattern for backward compatibility
const initActions = (componentDispatch: any) => {
  return Object.freeze({
    async loadAdvisorsAsync(uasId: number): Promise<AdvisorDto[]> {
      try {
        const result = await componentDispatch(loadAdvisorsAsync(uasId)).unwrap();
        return result.advisors;
      } catch (e) {
        componentDispatch(addExceptionNotification(e, 'Error while loading advisors'));
        return [];
      }
    },
    async getAdvisorsAsync(uasId: number): Promise<AdvisorDto[]> {
      try {
        const result = await componentDispatch(getAdvisorsAsync(uasId)).unwrap();
        return result.advisors;
      } catch (e) {
        componentDispatch(addExceptionNotification(e, 'Error while loading advisors'));
        return [];
      }
    },
  });
};

export type AdvisorSelectionActions = ReturnType<typeof initActions>;

export const advisorSelectionModule = {
  initActions: (dispatch: any) => initActions(dispatch),
  reducer: advisorSelectionSlice.reducer,
};
